import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Download, Printer, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DateGroupItem {
  date: string;
  total_commission: number;
  paid_commission: number;
  pending_commission: number;
  invoice_count: number;
  referrals: {
    id: string;
    referrer_name: string;
    invoice_number: string;
    patient_name: string;
    commission_amount: number;
    payment_status: string;
  }[];
}

export default function PCInvoiceDateGroup() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<DateGroupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadDateGroupReport();
  }, [startDate, endDate]);

  const loadDateGroupReport = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockDateGroupReport());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('referral_accruals')
        .select(`
          id,
          referrer_name,
          service_date,
          fee_amount,
          status,
          invoice:invoices(invoice_number),
          patient:patients(name)
        `);

      if (startDate) {
        query = query.gte('service_date', startDate);
      }
      if (endDate) {
        query = query.lte('service_date', endDate);
      }

      const { data, error } = await query.order('service_date', { ascending: false });
      if (error) throw error;

      const grouped = (data || []).reduce((acc: Record<string, any>, item) => {
        const date = item.service_date;
        if (!acc[date]) {
          acc[date] = {
            date,
            total_commission: 0,
            paid_commission: 0,
            pending_commission: 0,
            invoice_count: 0,
            referrals: [],
          };
        }
        
        acc[date].total_commission += item.fee_amount;
        if (item.status === 'paid') {
          acc[date].paid_commission += item.fee_amount;
        } else {
          acc[date].pending_commission += item.fee_amount;
        }
        acc[date].invoice_count += 1;
        acc[date].referrals.push({
          id: item.id,
          referrer_name: item.referrer_name,
          invoice_number: item.invoice?.invoice_number || 'N/A',
          patient_name: item.patient?.name || 'N/A',
          commission_amount: item.fee_amount,
          payment_status: item.status,
        });
        
        return acc;
      }, {});

      setReportData(Object.values(grouped));
    } catch (error) {
      console.error('Error loading date group report:', error);
      setReportData(getMockDateGroupReport());
    } finally {
      setLoading(false);
    }
  };

  const getMockDateGroupReport = (): DateGroupItem[] => {
    return [
      {
        date: '2025-01-27',
        total_commission: 1500,
        paid_commission: 1500,
        pending_commission: 0,
        invoice_count: 3,
        referrals: [
          {
            id: '1',
            referrer_name: 'Dr. Ahmed Rahman',
            invoice_number: 'INV-2025-001',
            patient_name: 'John Doe',
            commission_amount: 500,
            payment_status: 'paid',
          },
          {
            id: '2',
            referrer_name: 'Dr. Fatima Khan',
            invoice_number: 'INV-2025-002',
            patient_name: 'Jane Smith',
            commission_amount: 600,
            payment_status: 'paid',
          },
          {
            id: '3',
            referrer_name: 'City Diagnostic Center',
            invoice_number: 'INV-2025-003',
            patient_name: 'Ahmed Khan',
            commission_amount: 400,
            payment_status: 'paid',
          },
        ],
      },
      {
        date: '2025-01-26',
        total_commission: 950,
        paid_commission: 500,
        pending_commission: 450,
        invoice_count: 2,
        referrals: [
          {
            id: '4',
            referrer_name: 'Dr. Rahman Ali',
            invoice_number: 'INV-2025-004',
            patient_name: 'Sarah Ahmed',
            commission_amount: 500,
            payment_status: 'paid',
          },
          {
            id: '5',
            referrer_name: 'Health Plus Clinic',
            invoice_number: 'INV-2025-005',
            patient_name: 'Mohammed Khan',
            commission_amount: 450,
            payment_status: 'pending',
          },
        ],
      },
    ];
  };

  const toggleDate = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  const totalCommission = reportData.reduce((sum, item) => sum + item.total_commission, 0);
  const totalPaid = reportData.reduce((sum, item) => sum + item.paid_commission, 0);
  const totalPending = reportData.reduce((sum, item) => sum + item.pending_commission, 0);

  const handleExportCSV = () => {
    const rows: string[][] = [];
    rows.push(['Date', 'Referrer Name', 'Invoice #', 'Patient', 'Commission Amount', 'Status']);
    
    reportData.forEach(dateGroup => {
      dateGroup.referrals.forEach(referral => {
        rows.push([
          dateGroup.date,
          referral.referrer_name,
          referral.invoice_number,
          referral.patient_name,
          referral.commission_amount.toString(),
          referral.payment_status,
        ]);
      });
    });

    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pc-invoice-date-group-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">P.C Invoice Date Group</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Commission</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(totalCommission)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Paid Commission</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalPaid)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Pending Commission</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(totalPending)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="px-6 py-8 text-center text-gray-500">Loading...</div>
          ) : reportData.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">No data found</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {reportData.map((dateGroup) => (
                <div key={dateGroup.date}>
                  <div
                    className="px-6 py-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleDate(dateGroup.date)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {expandedDates.has(dateGroup.date) ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        )}
                        <div>
                          <p className="text-lg font-semibold text-gray-900">
                            {new Date(dateGroup.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                          <p className="text-sm text-gray-500">{dateGroup.invoice_count} invoices</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-lg font-bold text-blue-600">
                            {formatCurrency(dateGroup.total_commission)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Paid</p>
                          <p className="text-lg font-bold text-green-600">
                            {formatCurrency(dateGroup.paid_commission)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Pending</p>
                          <p className="text-lg font-bold text-orange-600">
                            {formatCurrency(dateGroup.pending_commission)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {expandedDates.has(dateGroup.date) && (
                    <div className="px-6 py-4">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Referrer Name
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Invoice #
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Patient
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Commission
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {dateGroup.referrals.map((referral) => (
                            <tr key={referral.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{referral.referrer_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{referral.invoice_number}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{referral.patient_name}</td>
                              <td className="px-4 py-3 text-sm text-blue-600 font-bold">
                                {formatCurrency(referral.commission_amount)}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    referral.payment_status === 'paid'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                >
                                  {referral.payment_status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
